import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ClipboardList, Brain, FileText, X, Stethoscope, ActivitySquare, User, Lock, BookOpen, ShieldCheck } from "lucide-react";
import { useFeatureTimer } from "../lib/analytics";
import { listReferences } from "../lib/api";

/**
 * Homepage-only refinement pass — matches attached mockup exactly.
 * All typography, color, spacing, and Google Font imports are scoped to this page
 * via a single inline <style> block. No global CSS is touched.
 */

const C = {
  bg: "#F7F4EE",
  card: "#FFFEFB",
  text: "#17191B",
  muted: "#55585C",
  burgundy: "#8A1721",
  navy: "#06233F",
  border: "#D8D1C7",
  red: "#E3313B",
};

export default function Welcome() {
  useFeatureTimer("welcome");
  const navigate = useNavigate();
  const [showPracticePicker, setShowPracticePicker] = useState(false);
  const [refCount, setRefCount] = useState(null);

  useEffect(() => {
    listReferences().then((rs) => setRefCount(rs.length)).catch(() => setRefCount(null));
  }, []);

  const pickPractice = (kind) => {
    setShowPracticePicker(false);
    navigate(`/sim?activity=${kind}&mode=standard`);
  };

  return (
    <div className="rn-home">
      {/* ================= Homepage-scoped styles (do NOT bleed to other pages) ================= */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');
        .rn-home { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', Arial, sans-serif; min-height: 100vh; -webkit-font-smoothing: antialiased; }
        .rn-home .serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        .rn-home .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .rn-home .eyebrow { font-family: 'Inter', sans-serif; text-transform: uppercase; letter-spacing: 0.14em; font-weight: 600; font-size: 12px; color: ${C.burgundy}; }
        .rn-home .wrap { max-width: 1320px; margin: 0 auto; padding-left: 20px; padding-right: 20px; }
        @media (min-width: 768px) { .rn-home .wrap { padding-left: 36px; padding-right: 36px; } }
        @media (min-width: 1120px) { .rn-home .wrap { padding-left: 96px; padding-right: 96px; } }
        .rn-home a, .rn-home button { transition: color .15s ease, background-color .15s ease, border-color .15s ease; }
        .rn-home .nav-link { color: ${C.text}; font-size: 16px; font-weight: 500; background: transparent; }
        .rn-home .nav-link:hover { color: ${C.burgundy}; }
        .rn-home .hero-h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; letter-spacing: -0.005em; line-height: 0.98; font-size: clamp(48px, 6.4vw, 76px); max-width: 620px; color: ${C.text}; margin: 0; }
        .rn-home .hero-lead { font-size: clamp(18px, 1.5vw, 21px); line-height: 1.42; color: ${C.text}; max-width: 520px; margin: 24px 0 0 0; }
        .rn-home .cta-primary { background: ${C.burgundy}; color: #fff; padding: 12px 28px; border-radius: 6px; font-weight: 500; font-size: 15px; border: 0; cursor: pointer; }
        .rn-home .cta-primary:hover { background: #71121B; }
        .rn-home .cta-link { color: ${C.burgundy}; font-weight: 500; font-size: 15px; border-bottom: 1px solid ${C.burgundy}; padding-bottom: 3px; background: transparent; }
        .rn-home .cta-link:hover { color: #71121B; border-color: #71121B; }
        .rn-home .card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 12px; padding: 32px; display: flex; flex-direction: column; text-align: left; cursor: pointer; }
        .rn-home .card:hover { border-color: ${C.burgundy}; }
        .rn-home .card h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 30px; line-height: 1.05; color: ${C.text}; margin: 6px 0 12px 0; }
        .rn-home .card p { font-size: 16px; line-height: 1.45; color: ${C.muted}; margin: 0; }
        .rn-home .card-cta { color: ${C.burgundy}; font-weight: 500; font-size: 15px; display: inline-flex; align-items: center; gap: 8px; margin-top: 22px; }
        .rn-home .card:hover .card-cta svg { transform: translateX(3px); }
        .rn-home .card-cta svg { transition: transform .15s ease; }
        .rn-home .scribe { background: ${C.navy}; border-radius: 12px; padding: 36px 40px; color: #fff; }
        .rn-home .scribe-beta { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border: 1px solid #2a3f60; border-radius: 999px; font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #eef2f7; }
        .rn-home .scribe h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 34px; line-height: 1.05; color: #fff; margin: 16px 0 8px 0; }
        .rn-home .scribe p { font-size: 15px; line-height: 1.5; color: #c4cdd9; margin: 0; max-width: 460px; }
        .rn-home .scribe-btn { background: #fff; color: ${C.text}; padding: 10px 22px; border-radius: 6px; font-weight: 500; font-size: 14px; border: 0; cursor: pointer; }
        .rn-home .scribe-btn:hover { background: #f2ede2; }
        .rn-home .scribe-warn { color: #98a3b3; font-size: 13px; display: inline-flex; align-items: center; gap: 8px; }
        .rn-home .scribe-warn .dot { width: 6px; height: 6px; border-radius: 999px; background: ${C.red}; }
        .rn-home .trust-item { display: flex; align-items: flex-start; gap: 14px; }
        .rn-home .trust-item p { font-size: 14px; line-height: 1.4; color: ${C.text}; margin: 0; }
        .rn-home .divider-v { border-left: 1px solid ${C.border}; }
        .rn-home .footer { border-top: 1px solid ${C.border}; }
        .rn-home .footer .brand { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 22px; color: ${C.text}; }
        .rn-home .footer a { color: ${C.text}; font-size: 15px; }
        .rn-home .footer a:hover { color: ${C.burgundy}; }
        .rn-home .timer { font-family: 'JetBrains Mono', monospace; font-weight: 600; letter-spacing: 0.05em; }
      `}</style>

      {/* ================= Nav ================= */}
      <header style={{ borderBottom: `1px solid ${C.border}`, background: C.bg }}>
        <div className="wrap" style={{ height: 72, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", background: "transparent", border: 0, cursor: "pointer", padding: 0 }} data-testid="home-logo">
            <img src="/project-rn-logo-horizontal.png" alt="Project RN" style={{ height: 40, width: "auto", display: "block" }} />
          </button>
          <nav style={{ display: "flex", alignItems: "center", gap: 42 }} className="rn-home-nav">
            <button onClick={() => setShowPracticePicker(true)} data-testid="nav-practice" className="nav-link" style={{ border: 0, cursor: "pointer" }}>Practice</button>
            <button onClick={() => navigate("/sim?activity=understand_concept&mode=standard")} data-testid="nav-understand" className="nav-link" style={{ border: 0, cursor: "pointer" }}>Understand</button>
            <button onClick={() => navigate("/refs")} data-testid="nav-references" className="nav-link" style={{ border: 0, cursor: "pointer" }}>References</button>
            <button onClick={() => navigate("/about")} data-testid="nav-about" className="nav-link" style={{ border: 0, cursor: "pointer" }}>About</button>
          </nav>
        </div>
      </header>

      {/* ================= Hero ================= */}
      <section className="wrap" style={{ paddingTop: 52, paddingBottom: 32 }}>
        <div className="eyebrow">Project RN · ICU</div>
        <h1 className="hero-h1" style={{ marginTop: 22 }}>Critical thinking,<br />under pressure.</h1>
        <p className="hero-lead">Practice high-stakes decisions. Understand the why.<br />Find what you need—fast.</p>
        <div style={{ marginTop: 32, display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <button onClick={() => navigate("/sim?activity=daily_case&mode=standard")} data-testid="hero-try-today" className="cta-primary">Try today&#39;s case</button>
          <button onClick={() => navigate("/refs")} data-testid="hero-open-refs" className="cta-link" style={{ border: 0 }}>Open quick references</button>
        </div>
      </section>

      {/* ================= Primary Cards ================= */}
      <section className="wrap" style={{ marginTop: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }} className="rn-home-cards">
          <button onClick={() => setShowPracticePicker(true)} data-testid="card-practice" className="card">
            <ClipboardList size={32} strokeWidth={1.3} color={C.burgundy} />
            <div className="eyebrow" style={{ marginTop: 20 }}>Practice</div>
            <h3>Practice a Case</h3>
            <p>One decision at a time. Immediate feedback. Real ICU pressure.</p>
            <span className="card-cta">Start a case <ArrowRight size={16} strokeWidth={2} /></span>
          </button>
          <button onClick={() => navigate("/sim?activity=understand_concept&mode=standard")} data-testid="card-understand" className="card">
            <Brain size={32} strokeWidth={1.3} color={C.burgundy} />
            <div className="eyebrow" style={{ marginTop: 20 }}>Understand</div>
            <h3>Understand a Concept</h3>
            <p>Clear physiology, pharmacology, and bedside reasoning — at the depth you choose.</p>
            <span className="card-cta">Ask Project RN <ArrowRight size={16} strokeWidth={2} /></span>
          </button>
          <button onClick={() => navigate("/refs")} data-testid="card-reference" className="card">
            <FileText size={32} strokeWidth={1.3} color={C.burgundy} />
            <div className="eyebrow" style={{ marginTop: 20 }}>Reference</div>
            <h3>Open a Reference</h3>
            <p>Fast, searchable ICU sheets for medications, devices, and critical-care concepts.</p>
            <span className="card-cta">{refCount != null ? `Browse ${refCount} ${refCount === 1 ? "sheet" : "sheets"}` : "Browse sheets"} <ArrowRight size={16} strokeWidth={2} /></span>
          </button>
        </div>
      </section>

      {/* ================= Code Scribe ================= */}
      <section className="wrap" style={{ marginTop: 20 }}>
        <div className="scribe" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 32, alignItems: "center" }}>
          <div>
            <div className="scribe-beta"><span style={{ width: 7, height: 7, borderRadius: 999, background: C.red, display: "inline-block" }} /> Beta · Simulation Use Only</div>
            <h3>Code Scribe</h3>
            <p>Giant timers, one-tap events, an automatic timeline, and a clean practice-code summary.</p>
            <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
              <button onClick={() => navigate("/code")} data-testid="code-scribe-explore" className="scribe-btn">Explore Code Scribe</button>
              <span className="scribe-warn"><span className="dot" /> Do not enter patient information</span>
            </div>
          </div>
          {/* Right-side illustration — hidden on mobile */}
          <div className="rn-scribe-illus" style={{ display: "flex", alignItems: "center", gap: 26 }}>
            <div style={{ border: `1px solid #2a3f60`, borderRadius: 6, padding: "14px 18px", background: "transparent" }}>
              <div className="timer" style={{ color: "#fff", fontSize: 44, lineHeight: 1, letterSpacing: "0.06em" }}>02:00</div>
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                <div style={{ height: 22, width: 22, border: `1px solid #2a3f60`, borderRadius: 3 }} />
                <div style={{ height: 22, width: 22, border: `1px solid #2a3f60`, borderRadius: 3, display: "grid", placeItems: "center" }}><X size={11} color="#5b6b83" /></div>
                <div style={{ height: 22, width: 22, border: `1px solid #2a3f60`, borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[24, 32, 28, 20].map((w, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: i === 2 ? C.red : "transparent", border: i === 2 ? "0" : `1px solid #5b6b83` }} />
                  <div style={{ height: 1, width: w * 4, background: "#4b5c78" }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= Trust Strip ================= */}
      <section className="wrap" style={{ marginTop: 48, marginBottom: 48 }}>
        <div className="rn-trust" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, alignItems: "center" }}>
          <div className="trust-item" style={{ padding: "6px 28px 6px 0" }}>
            <User size={22} strokeWidth={1.4} color={C.burgundy} />
            <p>Created by an<br />experienced critical-care nurse</p>
          </div>
          <div className="trust-item divider-v" style={{ padding: "6px 28px" }}>
            <ShieldCheck size={22} strokeWidth={1.4} color={C.burgundy} />
            <p>Clinically reviewed<br />education</p>
          </div>
          <div className="trust-item divider-v" style={{ padding: "6px 28px" }}>
            <Lock size={22} strokeWidth={1.4} color={C.burgundy} />
            <p>No patient<br />information</p>
          </div>
          <div className="trust-item divider-v" style={{ padding: "6px 28px" }}>
            <BookOpen size={22} strokeWidth={1.4} color={C.burgundy} />
            <p>Educational<br />use only</p>
          </div>
        </div>
      </section>

      {/* ================= Project RN Community ================= */}
      <section className="wrap" style={{ paddingTop: 8, paddingBottom: 48 }}>
        <div className="community" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "34px 40px", display: "grid", gridTemplateColumns: "1fr auto", gap: 40, alignItems: "center" }}>
          <div>
            <div className="eyebrow">Project RN Community</div>
            <h3 className="serif" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: 32, lineHeight: 1.1, color: C.text, margin: "10px 0 12px 0", maxWidth: 560 }}>
              Better nurses are built through better questions.
            </h3>
            <p style={{ fontSize: 16, lineHeight: 1.5, color: C.muted, margin: 0, maxWidth: 560 }}>
              Explore real clinical scenarios, sharpen your bedside reasoning, and learn alongside nurses committed to understanding the why—not simply memorizing the answer.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.5, color: C.text, margin: "14px 0 0 0", maxWidth: 560 }}>
              Real scenarios. Honest discussion. Practical knowledge you can carry to the bedside.
            </p>
            <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
              <a href="https://www.skool.com/projectrn-7210/about" target="_blank" rel="noopener noreferrer" data-testid="community-cta" className="cta-primary" style={{ textDecoration: "none", display: "inline-block" }}>
                Explore the community
              </a>
              <span style={{ fontSize: 13, color: C.muted }}>Built by a critical-care nurse. Built for the bedside.</span>
            </div>
          </div>
          <a href="https://www.skool.com/projectrn-7210/about" target="_blank" rel="noopener noreferrer" className="community-qr" data-testid="community-qr" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, lineHeight: 0 }}>
              <img src="/community-qr.png" alt="Scan to explore Project RN" width={148} height={148} style={{ display: "block", width: 148, height: 148 }} />
            </div>
            <span style={{ fontSize: 12, color: C.muted, letterSpacing: "0.06em" }}>Scan to explore Project RN</span>
          </a>
        </div>
      </section>

      {/* ================= Footer ================= */}
      <footer className="footer">
        <div className="wrap" style={{ paddingTop: 26, paddingBottom: 26, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div className="brand">Project RN</div>
          <nav style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
            <button onClick={() => navigate("/about")} data-testid="footer-about" style={{ background: "transparent", border: 0, cursor: "pointer", font: "inherit" }}><span style={{ color: C.text }}>About</span></button>
            <button onClick={() => navigate("/about#clinical-content")} data-testid="footer-clinical" style={{ background: "transparent", border: 0, cursor: "pointer", font: "inherit" }}><span style={{ color: C.text }}>Clinical content</span></button>
            <button onClick={() => navigate("/about#privacy")} data-testid="footer-privacy" style={{ background: "transparent", border: 0, cursor: "pointer", font: "inherit" }}><span style={{ color: C.text }}>Data &amp; privacy</span></button>
            <a href="https://www.skool.com/projectrn-7210/about" target="_blank" rel="noopener noreferrer" data-testid="footer-community" style={{ background: "transparent", border: 0, cursor: "pointer", font: "inherit", textDecoration: "none" }}><span style={{ color: C.text }}>Community</span></a>
          </nav>
        </div>
      </footer>

      {/* Mobile responsiveness overrides */}
      <style>{`
        @media (max-width: 900px) {
          .rn-scribe-illus { display: none !important; }
          .rn-home .scribe { grid-template-columns: 1fr !important; }
          .rn-trust { grid-template-columns: 1fr 1fr !important; gap: 20px 0 !important; }
          .rn-trust .divider-v:nth-child(3) { border-left: 0 !important; }
        }
        @media (max-width: 560px) {
          .rn-trust { grid-template-columns: 1fr !important; }
          .rn-trust .divider-v { border-left: 0 !important; border-top: 1px solid ${C.border}; padding-top: 20px !important; padding-left: 0 !important; }
          .rn-home-nav { gap: 20px !important; }
        }
      `}</style>

      {/* ================= Practice picker modal ================= */}
      {showPracticePicker && (
        <div className="rn-home" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 50, display: "grid", placeItems: "center", padding: 20 }} onClick={() => setShowPracticePicker(false)} data-testid="practice-picker-modal">
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", maxWidth: 520, padding: 32 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div className="eyebrow">Practice a Case</div>
                <h3 className="serif" style={{ fontSize: 26, fontWeight: 600, margin: "6px 0 6px 0", color: C.text, lineHeight: 1.15 }}>Daily Case or Build Your Own Simulation?</h3>
              </div>
              <button onClick={() => setShowPracticePicker(false)} data-testid="practice-picker-close" style={{ padding: 6, background: "transparent", border: 0, cursor: "pointer" }}><X size={16} /></button>
            </div>
            <p style={{ marginTop: 6, marginBottom: 22, color: C.muted, fontSize: 14 }}>Pick your training track.</p>
            <div style={{ display: "grid", gap: 12 }}>
              <button onClick={() => pickPractice("daily_case")} data-testid="pick-daily-case" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 18, textAlign: "left", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <ActivitySquare size={18} strokeWidth={1.6} color={C.burgundy} />
                  <div style={{ flex: 1 }}>
                    <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: C.text }}>Daily Case</div>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>High-pressure ICU decision training from the scenario library.</div>
                  </div>
                </div>
              </button>
              <button onClick={() => pickPractice("simulation_drill")} data-testid="pick-simulation-drill" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 18, textAlign: "left", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Stethoscope size={18} strokeWidth={1.6} color={C.burgundy} />
                  <div style={{ flex: 1 }}>
                    <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: C.text }}>Build Your Own Simulation</div>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>User-driven scenario. You give me the situation. I build the case.</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
